/**
 * Unified ContentService — resolver (routing policy + fallback chain).
 *
 * Given a `ContentRef`, the resolver:
 *   1. checks the single cache (honoring CloudFront Expires / presign expiry)
 *   2. builds the per-namespace ordered source chain from the env policy
 *   3. runs each adapter in order, returning the first usable handle
 *   4. caches the resolved URL for reuse
 *
 * The chain order encodes the architecture decision in
 * docs/content-service-design.md §3.3: prefer the fastest/most-cacheable source
 * (dev disk, static), fall back to signed CloudFront, then to the S3 stream
 * proxy / backend proxy. This mirrors the 3-tier fallback the memory notes are
 * required for ("never trust one proxy — fall through to a public source").
 */

import { getContentConfig } from './config.js';
import { getNamespaceConfig } from './namespaceConfig.js';
import {
  localDiskAdapter,
  staticAdapter,
  cloudFrontSignedAdapter,
  s3StreamAdapter,
  backendProxyAdapter,
} from './adapters.js';

/** Ordered adapter chain for a namespace given the current env config. */
export function buildChain(namespace, cfg) {
  switch (namespace) {
    case 'gallery': {
      const c = [];
      if (cfg.localDisk) c.push(localDiskAdapter);
      c.push(cloudFrontSignedAdapter, s3StreamAdapter);
      return c;
    }
    case 'hospitality': {
      const c = [];
      if (cfg.localDisk) c.push(localDiskAdapter);
      c.push(staticAdapter, backendProxyAdapter);
      return c;
    }
    case 'curriculum':
      return [cloudFrontSignedAdapter, staticAdapter];
    case 'fsi': {
      const c = [];
      if (cfg.localDisk) c.push(localDiskAdapter);
      c.push(s3StreamAdapter, cloudFrontSignedAdapter, backendProxyAdapter);
      return c;
    }
    case 'studio':
      return [staticAdapter, cloudFrontSignedAdapter, s3StreamAdapter];
    case 'photoAlbum':
      return [cloudFrontSignedAdapter];
    case 'radio':
      return [backendProxyAdapter];
    case 'kb':
      return [staticAdapter];
    default:
      return [staticAdapter];
  }
}

function hasUsableUrl(h) {
  return !!(h && typeof h.url === 'string' && h.url.length > 0);
}

/**
 * In-flight dedup: collapse concurrent identical resolves into a single promise
 * so N components requesting the same ContentRef at once trigger ONE sign
 * round-trip instead of N. Keyed by ref identity (namespace|logicalKey|variant);
 * differing opts.signFn for the same ref are not expected in practice.
 */
const inflight = new Map();
function inflightKey(ref) {
  return `${ref.namespace}|${ref.logicalKey}|${ref.variant || '*'}`;
}

/**
 * @param {import('./types.js').ContentRef} ref
 * @param {object} [opts]
 * @param {import('./cache.js').ContentCache} [opts.cache]
 * @param {(endpoint:string, ref:any) => Promise<string|null>} [opts.signFn]
 * @param {(ref:any, h:any) => boolean} [opts.validateFn]   optional; false → try next
 * @param {boolean} [opts.skipCache]
 * @param {(name:string)=>Array<Function>} [opts.chainFor]  injectable (tests)
 */
export async function resolve(ref, opts = {}) {
  const cfg = getContentConfig();
  const ns = getNamespaceConfig(ref.namespace);
  if (!ns) throw new Error(`ContentService: unknown namespace '${ref.namespace}'`);
  const cache = opts.cache;

  const variant = ref.variant || '*';

  if (cache && !opts.skipCache) {
    const hit = cache.getHandle(ref.namespace, ref.logicalKey, variant);
    if (hit) return { ...hit, cached: true };
  }

  // In-flight dedup: if an identical resolve is already running, share it.
  const key = inflightKey(ref);
  const existing = inflight.get(key);
  if (existing) return existing;

  const run = (async () => {
    try {
      const chain = (opts.chainFor ? opts.chainFor(ref.namespace) : buildChain(ref.namespace, cfg));
      let lastError = null;
      for (const adapter of chain) {
        let h;
        try {
          h = await adapter(ref, cfg, ns, { signFn: opts.signFn });
        } catch (e) {
          lastError = e;
          h = null;
        }
        if (!hasUsableUrl(h)) continue;
        if (opts.validateFn && !opts.validateFn(ref, h)) {
          lastError = new Error('validateFn rejected handle');
          continue;
        }
        if (cache && !opts.skipCache) {
          cache.set(ref.namespace, ref.logicalKey, variant, h.url, h.ttl || 0, {
            isStream: h.isStream,
            source: h.source,
            expiresAt: h.expiresAt,
          });
        }
        return { ...h, cached: false };
      }

      const reason = lastError ? lastError.message : 'all sources returned no handle';
      throw new Error(`ContentService: failed to resolve ${ref.namespace}/${ref.logicalKey} — ${reason}`);
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, run);
  return run;
}

/**
 * Synchronous best-effort URL. Returns a cached hit OR a non-signed candidate
 * (disk/static/stream/backend-proxy) without performing the async sign step.
 * Returns null when the only viable source requires signing (caller must
 * `await resolve()` instead).
 */
export function resolveSync(ref, { cache } = {}) {
  const cfg = getContentConfig();
  const ns = getNamespaceConfig(ref.namespace);
  if (!ns) return null;
  const variant = ref.variant || '*';

  if (cache) {
    const hit = cache.get(ref.namespace, ref.logicalKey, variant);
    if (hit) return hit;
  }

  // Only non-signed sources can be resolved synchronously.
  const syncAdapters = [localDiskAdapter, staticAdapter, s3StreamAdapter, backendProxyAdapter];
  for (const adapter of syncAdapters) {
    const h = adapter(ref, cfg, ns, {});
    if (hasUsableUrl(h)) return h.url;
  }
  return null;
}
